import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, onSnapshot, getDoc, collection, getDocs } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { BLIND_STRUCTURE_DEFAULTS } from '../../models/blindStructure';
import { PayoutEntry } from '../../components/TournamentClock/clock.types';
import QRCode from 'qrcode';
import { DEFAULT_PAYOUTS, CLOCK_FONTS as F } from '../../components/TournamentClock/clock.constants';

// ── Staff access check ───────────────────────────────────────────────────────
const useStaffAccess = (eventId: string | undefined) => {
  const [access, setAccess] = React.useState<'none' | 'view' | 'control'>('none');
  const [staffName, setStaffName] = React.useState('');
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    if (!eventId) { setChecked(true); return; }
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setAccess('none'); setChecked(true); return; }
      try {
        // Check if user is TD (admin)
        const evSnap = await getDoc(doc(db, 'events', eventId));
        if (evSnap.exists() && evSnap.data().admin_uid === user.uid) {
          setAccess('control'); setStaffName('TD'); setChecked(true); return;
        }
        // Check staff subcollection
        const staffSnap = await getDoc(doc(db, 'events', eventId, 'staff', user.uid));
        if (staffSnap.exists()) {
          const staffData = staffSnap.data();
          setAccess(staffData.clockAccess ?? 'view');
          setStaffName(staffData.name ?? '');
          // Mark as checked in
          import('firebase/firestore').then(({ updateDoc, doc: d }) => {
            updateDoc(d(db, 'events', eventId, 'staff', user.uid), {
              status: 'checked_in',
              checkedInAt: new Date().toISOString(),
            }).catch(() => {});
          });
          setChecked(true);
          return;
        }
      } catch (e) {}
      setAccess('none');
      setChecked(true);
    });
    return () => unsub();
  }, [eventId]);

  return { access, staffName, checked };
};

const safeStr = (val: any): string => {
  if (!val && val !== 0) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') return val.label || val.value || '';
  return String(val);
};

const formatTime = (seconds: number): string => {
  const m = Math.floor(Math.max(0, seconds) / 60);
  const s = Math.max(0, seconds) % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

type ClockDoc = {
  state: string;
  currentLevelIndex: number;
  secondsLeft: number;
  isRunning: boolean;
  eventName: string;
  updatedAt: number;
  breakEndsAt: number | null;
  startingChips?: number;
  blindLevelDuration?: number;
  buyIn?: any;
  maxPlayers?: number;
  payouts?: PayoutEntry[];
};

const PayoutScroller: React.FC<{ payouts: PayoutEntry[] }> = ({ payouts }) => {
  const doubled = [...payouts, ...payouts];
  return (
    <div style={{ background: '#1A1A1A', overflow: 'hidden', height: 34, display: 'flex', alignItems: 'center', borderTop: '1px solid #222', flexShrink: 0 }}>
      <div style={{ background: '#333', color: '#DDD', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', fontFamily: F.ui, padding: '0 14px', height: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' as const, flexShrink: 0, textTransform: 'uppercase' as const, borderRight: '1px solid #444' }}>
        &#127942; Payouts
      </div>
      <div style={{ overflow: 'hidden', flex: 1, height: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', animation: 'pcTicker 24s linear infinite' }}>
          {doubled.map((p, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 20px' }}>
                <span style={{ color: '#DDD', fontSize: 12, fontWeight: 600, fontFamily: F.ui }}>{p.position}</span>
                <span style={{ color: '#D4A017', fontSize: 12, fontWeight: 700, fontFamily: F.ui }}>{p.amount}</span>
              </div>
              <div style={{ width: 3, height: 3, borderRadius: '50%', background: '#444', flexShrink: 0 }} />
            </React.Fragment>
          ))}
        </div>
        <style>{`@keyframes pcTicker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
      </div>
    </div>
  );
};

const GuestCapture: React.FC<{ eventId: string }> = ({ eventId }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { doc, setDoc, collection } = await import('firebase/firestore');
      const guestRef = doc(collection(db, 'events', eventId, 'guests'));
      await setDoc(guestRef, { name: name.trim(), phone: phone.trim(), joinedAt: Date.now() });
      setSaved(true);
    } catch { /* silent fail — guest capture is optional */ }
    setSaving(false);
  };

  if (saved) return (
    <div style={{ textAlign: 'center', padding: '16px 20px', background: '#0D1F1E', borderTop: '1px solid #134E4A' }}>
      <span style={{ color: '#5EEAD4', fontSize: 14, fontFamily: F.ui }}>&#10003; You're on the list, {name}!</span>
    </div>
  );

  return (
    <div style={{ background: '#111', borderTop: '1px solid #1E1E1E', padding: '14px 20px' }}>
      <div style={{ color: '#666', fontSize: 10, letterSpacing: '0.18em', fontFamily: F.ui, textTransform: 'uppercase' as const, marginBottom: 10 }}>
        Want break reminders &amp; next game updates?
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Your name"
          style={{ flex: '1 1 120px', background: '#1A1A1A', border: '1px solid #333', borderRadius: 5, padding: '8px 12px', color: '#DDD', fontSize: 13, fontFamily: F.ui, outline: 'none' }}
        />
        <input
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="Phone (optional)"
          type="tel"
          style={{ flex: '1 1 140px', background: '#1A1A1A', border: '1px solid #333', borderRadius: 5, padding: '8px 12px', color: '#DDD', fontSize: 13, fontFamily: F.ui, outline: 'none' }}
        />
        <button
          onClick={handleSave}
          disabled={!name.trim() || saving}
          style={{ background: name.trim() ? '#052E16' : '#1A1A1A', color: name.trim() ? '#86EFAC' : '#555', border: `1px solid ${name.trim() ? '#166534' : '#333'}`, borderRadius: 5, padding: '8px 18px', fontSize: 13, fontFamily: F.ui, cursor: name.trim() ? 'pointer' : 'default', fontWeight: 600 }}
        >
          {saving ? '...' : "I'm in"}
        </button>
      </div>
    </div>
  );
};

const QRModal: React.FC<{ url: string; onClose: () => void }> = ({ url, onClose }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  React.useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, url, {
        width: 260,
        margin: 2,
        color: { dark: '#111111', light: '#FFFFFF' },
      });
    }
  }, [url]);
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: 24 }}
    >
      <div onClick={e => e.stopPropagation()} style={{ background: '#FFF', borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, maxWidth: 320, width: '100%' }}>
        <div style={{ color: '#111', fontSize: 16, fontWeight: 700, fontFamily: 'Arial, sans-serif', letterSpacing: '0.05em' }}>
          poker<span style={{ color: '#888' }}>city</span>&#9827; live
        </div>
        <canvas ref={canvasRef} style={{ borderRadius: 8 }} />
        <div style={{ color: '#888', fontSize: 12, fontFamily: 'Arial, sans-serif', textAlign: 'center', wordBreak: 'break-all' as const }}>{url}</div>
        <button
          onClick={onClose}
          style={{ background: '#111', color: '#FFF', border: 'none', borderRadius: 8, padding: '10px 32px', fontSize: 14, fontFamily: 'Arial, sans-serif', fontWeight: 600, cursor: 'pointer', width: '100%' }}
        >
          Close
        </button>
      </div>
      <div style={{ color: '#666', fontSize: 12, fontFamily: 'Arial, sans-serif' }}>Tap outside to close</div>
    </div>
  );
};

const LiveClockPage: FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [showQR, setShowQR] = useState(false);
  const [clockDoc, setClockDoc] = useState<ClockDoc | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState<number>(0);
  const [levels, setLevels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const lastSyncRef = React.useRef<{ secondsLeft: number; syncedAt: number; isRunning: boolean }>({ secondsLeft: 0, syncedAt: 0, isRunning: false });

  // Build levels from clock doc data
  useEffect(() => {
    if (!clockDoc) return;
    const chips = clockDoc.startingChips || 10000;
    const duration = clockDoc.blindLevelDuration || 20;
    const structure = BLIND_STRUCTURE_DEFAULTS[chips] || BLIND_STRUCTURE_DEFAULTS[10000];
    const built = structure.items.map((item: string, index: number) => {
      if (item === 'Break') return { index, isBreak: true, label: 'BREAK', duration: 15 };
      const parts = item.split('-');
      const sb = parseInt(parts[0].replace(/,/g, ''));
      const bb = parseInt(parts[1].replace(/,/g, ''));
      return { index, isBreak: false, label: item, smallBlind: sb, bigBlind: bb, ante: Math.floor(bb * 0.1), duration };
    });
    setLevels(built);
  }, [clockDoc?.startingChips, clockDoc?.blindLevelDuration]);

  // Subscribe to Firestore clock state
  useEffect(() => {
    if (!eventId) { setError('Invalid link'); setLoading(false); return; }
    const clockRef = doc(db, 'events', eventId, 'clock', 'state');
    const unsub = onSnapshot(clockRef, (snap) => {
      if (!snap.exists()) {
        setError('Game not started yet — check back when the TD starts the clock.');
        setLoading(false);
        return;
      }
      const data = snap.data() as ClockDoc;
      setClockDoc(data);
      lastSyncRef.current = { secondsLeft: data.secondsLeft || 0, syncedAt: Date.now(), isRunning: data.isRunning || false };
      setDisplaySeconds(data.secondsLeft || 0);
      setLoading(false);
    }, () => { setError('Unable to connect. Check your internet.'); setLoading(false); });
    return () => unsub();
  }, [eventId]);

  // Local countdown: interpolate between Firestore syncs
  useEffect(() => {
    const tick = setInterval(() => {
      const { secondsLeft, syncedAt, isRunning } = lastSyncRef.current;
      if (!isRunning) return;
      const elapsed = Math.floor((Date.now() - syncedAt) / 1000);
      const estimated = Math.max(0, secondsLeft - elapsed);
      setDisplaySeconds(estimated);
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#555', fontSize: 14, fontFamily: F.ui, letterSpacing: '0.2em' }}>CONNECTING...</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
      <div style={{ color: '#FFF', fontSize: 18, fontFamily: F.ui, fontWeight: 700 }}>poker<span style={{ color: '#AAA' }}>city</span>&#9827;</div>
      <div style={{ color: '#888', fontSize: 14, fontFamily: F.ui, textAlign: 'center', maxWidth: 320 }}>{error}</div>
    </div>
  );

  if (!clockDoc || levels.length === 0) return null;

  const idx = Math.min(clockDoc.currentLevelIndex || 0, levels.length - 1);
  const currentLevel = levels[idx];
  const nextLevel = levels[idx + 1];
  const isBreak = currentLevel?.isBreak ?? false;
  const isRunning = clockDoc.isRunning;
  const isWarning = displaySeconds <= 60 && displaySeconds > 0 && !isBreak && isRunning;
  const isCritical = displaySeconds <= 10 && displaySeconds > 0 && !isBreak && isRunning;
  const payouts: PayoutEntry[] = clockDoc.payouts || DEFAULT_PAYOUTS;
  const buyIn = clockDoc.buyIn ? `$${safeStr(clockDoc.buyIn)}` : '';
  const timerColor = isCritical ? '#EF4444' : isWarning ? '#F59E0B' : isBreak ? '#FFF' : '#1A1A1A';
  const nextLevelText = nextLevel ? (nextLevel.isBreak ? `Break (${nextLevel.duration} min)` : `${nextLevel.smallBlind?.toLocaleString()} / ${nextLevel.bigBlind?.toLocaleString()}${nextLevel.ante ? ` · ANTE ${nextLevel.ante.toLocaleString()}` : ''}`) : 'Final level';

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 0 40px 0' }}>
      {/* Header */}
      <div style={{ width: '100%', maxWidth: 520, background: '#181818', borderBottom: '1px solid #222', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#AAA', fontSize: 16, fontWeight: 700, fontFamily: F.ui }}>poker</span>
          <span style={{ color: '#FFF', fontSize: 16, fontWeight: 700, fontFamily: F.ui }}>city</span>
          <span style={{ color: '#555', fontSize: 13 }}>&#9827;</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: isRunning ? '#22C55E' : '#555', boxShadow: isRunning ? '0 0 6px #22C55E' : 'none' }} />
          <span style={{ color: '#666', fontSize: 11, fontFamily: F.ui, letterSpacing: '0.12em' }}>{isRunning ? 'LIVE' : clockDoc.state === 'break' ? 'BREAK' : 'PAUSED'}</span>
        </div>
      </div>

      {/* Clock body */}
      <div style={{ width: '100%', maxWidth: 520, flex: 1 }}>
        <div style={{ background: isBreak ? '#111' : 'rgba(244,244,244,0.97)', borderRadius: 0, overflow: 'hidden' }}>

          {/* Level badge + event name */}
          <div style={{ background: isBreak ? '#181818' : '#EBEBEB', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: isBreak ? '1px solid #222' : '1px solid #D8D8D8' }}>
            <div style={{ background: isBreak ? '#0D1F1E' : '#2E2E2E', color: isBreak ? '#5EEAD4' : '#DDD', fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 4, letterSpacing: '0.15em', fontFamily: F.ui }}>
              {isBreak ? 'BREAK' : `LEVEL ${idx + 1}`}
            </div>
            {clockDoc.eventName && <span style={{ color: isBreak ? '#666' : '#888', fontSize: 12, fontFamily: F.ui }}>{clockDoc.eventName}</span>}
            {!isRunning && !isBreak && (
              <div style={{ marginLeft: 'auto', background: '#2D0D0D', color: '#FCA5A5', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 4, letterSpacing: '0.12em', fontFamily: F.ui }}>PAUSED</div>
            )}
          </div>

          {/* Timer */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 20px 20px', gap: 8 }}>
            <div style={{ color: isBreak ? '#888' : '#888', fontSize: 10, letterSpacing: '0.18em', fontFamily: F.ui, textTransform: 'uppercase' as const }}>time remaining</div>
            <div style={{ fontSize: 96, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-3px', color: timerColor, fontFamily: F.digits, transition: 'color 0.3s' }}>
              {formatTime(displaySeconds)}
            </div>
            {isWarning && (
              <div style={{ background: '#FEF3C7', border: '1.5px solid #D97706', borderRadius: 5, padding: '4px 14px' }}>
                <span style={{ color: '#78350F', fontSize: 10, letterSpacing: '0.1em', fontFamily: F.ui, fontWeight: 700 }}>&#9888; LEVEL ENDING SOON</span>
              </div>
            )}
          </div>

          {/* Blinds */}
          {!isBreak && (
            <div style={{ padding: '0 20px 24px', display: 'flex', gap: 24, alignItems: 'flex-end' }}>
              <div>
                <div style={{ color: '#888', fontSize: 10, letterSpacing: '0.18em', fontFamily: F.ui, textTransform: 'uppercase' as const, marginBottom: 4 }}>blinds</div>
                <div style={{ fontSize: 40, fontWeight: 700, color: '#111', lineHeight: 1, fontFamily: F.digits }}>{currentLevel?.smallBlind?.toLocaleString()} / {currentLevel?.bigBlind?.toLocaleString()}</div>
              </div>
              {currentLevel?.ante ? (
                <div>
                  <div style={{ color: '#888', fontSize: 10, letterSpacing: '0.18em', fontFamily: F.ui, textTransform: 'uppercase' as const, marginBottom: 4 }}>ante</div>
                  <div style={{ fontSize: 26, fontWeight: 600, color: '#7C3AED', lineHeight: 1, fontFamily: F.digits }}>{currentLevel.ante.toLocaleString()}</div>
                </div>
              ) : null}
              {buyIn && (
                <div>
                  <div style={{ color: '#888', fontSize: 10, letterSpacing: '0.18em', fontFamily: F.ui, textTransform: 'uppercase' as const, marginBottom: 4 }}>buy-in</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: '#059669', lineHeight: 1, fontFamily: F.digits }}>{buyIn}</div>
                </div>
              )}
            </div>
          )}

          {/* Next level bar */}
          <div style={{ background: isBreak ? '#0D1F1E' : '#EBEBEB', padding: '9px 20px', display: 'flex', alignItems: 'center', gap: 8, borderTop: isBreak ? '1px solid #134E4A' : '1px solid #D8D8D8' }}>
            <span style={{ color: isBreak ? '#5EEAD4' : '#888', fontSize: 10, letterSpacing: '0.14em', fontFamily: F.ui, textTransform: 'uppercase' as const }}>next</span>
            <span style={{ color: isBreak ? '#5EEAD4' : '#AAA', fontSize: 14 }}>&#8594;</span>
            <span style={{ color: isBreak ? '#AAA' : '#444', fontSize: 14, fontWeight: 500, fontFamily: F.ui }}>{nextLevelText}</span>
          </div>
        </div>

        {/* Payout scroller */}
        <PayoutScroller payouts={payouts} />

        {/* Guest capture */}
        {eventId && <GuestCapture eventId={eventId} />}

        {/* Join CTA */}
        <div style={{ background: '#0A0A0A', padding: '20px', textAlign: 'center', borderTop: '1px solid #1A1A1A' }}>
          <div style={{ color: '#444', fontSize: 11, letterSpacing: '0.18em', fontFamily: F.ui, textTransform: 'uppercase' as const, marginBottom: 8 }}>Track your stats &amp; history</div>
          <a href="https://pokercity.com" style={{ color: '#D4A017', fontSize: 13, fontFamily: F.ui, fontWeight: 600, textDecoration: 'none', letterSpacing: '0.08em' }}>
            Create a free PokerCity account &#8594;
          </a>
        </div>
      </div>
      {showQR && <QRModal url={`https://pokercity.com/live/${eventId}`} onClose={() => setShowQR(false)} />}
      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
        <button
          onClick={() => setShowQR(true)}
          style={{ background: '#D4A017', color: '#111', border: 'none', borderRadius: '50%', width: 56, height: 56, cursor: 'pointer', boxShadow: '0 4px 20px rgba(212,160,23,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitAppearance: 'none' as any }}
          aria-label="Share QR code"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default LiveClockPage;
